import { PropsWithChildren, useContext, useMemo } from 'react';

import { Box, Link as ChakraLink, Flex, Grid, GridItem, Text, VStack } from '@chakra-ui/react';
import { formatDistanceToNow } from 'date-fns';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { Link as ReactRouterLink } from 'react-router-dom';

import { getFullName } from '../../gql/customerGql';
import {
  Deal,
  DealSourceEnum,
  DealSourceLabelMap,
  DealStateEnum,
  DealTypeLabelMap,
} from '../../gql/dealGql';
import { useOnDealLockStatusSubscription } from '../../gql/generated/graphql';

import FollowUpBadge from '../NotificationBadge/FollowUpBadge';
import TextNotificationBadgeV2 from '../NotificationBadge/TextNotificationBadgeV2';
import DealTags from '../shared/DealTags';
import { PodColor } from '../shared/PodColor';
import Tooltip from '../shared/Tooltip';
import DealLockedTooltip from './components/DealLockedTooltip';

import {
  getAttributionLine,
  renderTaskDetails,
  shouldBlurDealInfo,
  showBootActions,
} from './Task.utils';
import { BootActions } from './TaskActions/BootAction';
import useDealColor from './hooks/useDealColor';

import colors from '../../chakra/foundations/colors';
import { LDFlags } from '../../constants/experiments';
import { getUnseenMessageNotificationsCount } from '../../constants/notifications';
import { PermissionEnum } from '../../constants/permissions';
import useFlag from '../../hooks/useFlag';
import { useUser } from '../../hooks/useUser';
import { AbilityContext } from '../../libs/contextLib';
import { User } from '../../types/user';
import { getIsClickable } from '../../utils/deals';

type LELinkProps = PropsWithChildren<{ to: string; isDisabled?: boolean }>;

const LELink = ({ to, isDisabled, children }: LELinkProps) => {
  return isDisabled ? (
    <>{children}</>
  ) : (
    <ChakraLink
      as={ReactRouterLink}
      to={to}
      _hover={{
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      {children}
    </ChakraLink>
  );
};

type BlurredTaskProps = {
  deal: Deal;
  abilities: Set<PermissionEnum>;
  user: User;
  autoAssigningDealsFlag: boolean;
  assumedId?: string;
  height?: string;
  width?: string;
};

const BlurredTask = ({
  deal,
  assumedId,
  abilities,
  user,
  autoAssigningDealsFlag,
  height = '100%',
  width = '100%',
}: BlurredTaskProps) => (
  <>
    <GridItem colSpan={6}>
      <VStack
        filter="auto"
        blur="5px"
        alignItems="flex-start"
        justifyContent="space-between"
        h="full"
      >
        <Box height={height} width={width} borderRadius="md">
          <Text fontWeight="bold" fontSize="lg" whiteSpace="nowrap">
            Customer Name
          </Text>
        </Box>
        <Box pb={2}>
          <Text fontSize="xs">Deal Owners</Text>
          <Text fontSize="2xs" color="gray.500" textTransform="uppercase">
            Deal Type and Source
          </Text>
        </Box>
      </VStack>
    </GridItem>
    <GridItem colSpan={1} py={2}>
      <VStack h="full">
        {renderTaskDetails(deal, assumedId, abilities, user, autoAssigningDealsFlag)}
      </VStack>
    </GridItem>
    <GridItem colSpan={7} borderTop="1px solid" borderColor="gray.300" pt={2}>
      <Flex justifyContent="flex-end" alignItems="center" h="full" pr={2}>
        <Tooltip
          label={formatDistanceToNow(
            deal.lane_arrival_utc ? new Date(deal.lane_arrival_utc) : new Date(),
          )}
          placement="top"
          px={2}
          py={1}
          bg="gray.800"
        >
          <Box>
            <AiOutlineClockCircle size={14} />
          </Box>
        </Tooltip>
      </Flex>
    </GridItem>
  </>
);

type TaskProps = {
  deal: Deal;
  assumedId?: string;
};

const Task = ({ deal, assumedId }: TaskProps) => {
  const autoAssigningFlag = useFlag(LDFlags.AUTO_ASSIGNING_DEALS);
  const autoAssigningDealsFlag = useFlag(LDFlags.AUTO_ASSIGNING_DEALS);
  const secureLinkFlag = useFlag(LDFlags.SECURE_INFO_LINK);
  const abilities = useContext(AbilityContext);
  const user = useUser();

  const isClickable = useMemo(
    () => getIsClickable(deal, abilities),
    [
      deal.source,
      deal.state,
      deal.closer_id,
      deal.closer?.id,
      deal.setter_id,
      deal.structuring_manager_id,
      deal.structuring_manager?.id,
      abilities,
    ],
  );

  const color = useDealColor({
    autoAssigningFlag,
    canClaimAsFinancialSpecialistInSoftClose: abilities.has(
      PermissionEnum.ClaimFinancialSpecialistInSoftClose,
    ),
    deal,
    secureLinkFlag,
  });

  const unseenNotificationsCount = useMemo(
    () => getUnseenMessageNotificationsCount(user.id, { notifications: deal.notifications }),
    [user.id, deal.notifications],
  );

  const shouldBlur = useMemo(
    () => shouldBlurDealInfo(deal, abilities, user),
    [deal, abilities, user],
  );

  useOnDealLockStatusSubscription({
    skip: !deal.id,
    variables: { dealId: Number(deal.id) },
    onData: ({ data }) => {
      if (!data.data?.onDealLockStatus) {
        return;
      }
      // The swimlanes are not in the DealContext, so we need to update a by reference
      // eslint-disable-next-line no-param-reassign
      deal.lock_status = data.data.onDealLockStatus.lockStatus ?? null;
    },
  });

  return (
    <Box
      bg={color}
      rounded="md"
      border="1px solid"
      borderColor="gray.200"
      shadow="sm"
      p={2}
      pl={3}
      position="relative"
      {...(color === colors.taskGrey && { opacity: '85%' })}
    >
      <Grid templateColumns="1px repeat(6, 1fr)" gridTemplateRows="1fr">
        {shouldBlur ? (
          <BlurredTask
            deal={deal}
            assumedId={assumedId}
            abilities={abilities}
            user={user}
            autoAssigningDealsFlag={autoAssigningDealsFlag}
          />
        ) : (
          <>
            <GridItem colSpan={1} rowSpan={2}>
              <PodColor w="5px" pod={deal.pod} roundedLeft="md" />
            </GridItem>
            <GridItem colSpan={5}>
              <LELink to={`/deals/${deal.id}`} isDisabled={!isClickable}>
                <VStack alignItems="flex-start" justifyContent="space-between" h="full">
                  <Text fontWeight="bold" fontSize="lg">
                    {getFullName(deal.customer)}
                  </Text>
                  <Box pb={2}>
                    <Text fontSize="xs">
                      {getAttributionLine({
                        deal,
                        separator: '|',
                        includeStructuringManager: true,
                      })}
                    </Text>
                    {deal.type && deal.source ? (
                      <Text fontSize="2xs" color="gray.500" textTransform="uppercase">
                        {DealTypeLabelMap[deal.type]} |{' '}
                        {DealSourceLabelMap[deal.source as DealSourceEnum]}
                      </Text>
                    ) : null}
                  </Box>
                </VStack>
              </LELink>
            </GridItem>
            <GridItem colSpan={1} py={2}>
              <VStack h="full" position="relative">
                {renderTaskDetails(deal, assumedId, abilities, user, autoAssigningDealsFlag)}
                <DealLockedTooltip deal={deal} secureLinkFlag={secureLinkFlag} />
                {deal.follow_up ? <FollowUpBadge followUp={deal.follow_up} size={20} /> : null}
                {unseenNotificationsCount ? (
                  <TextNotificationBadgeV2
                    count={unseenNotificationsCount}
                    deal={deal}
                    size={24}
                    showSimplifiedCount
                  />
                ) : null}
                {deal.state !== DealStateEnum.StructuringInProgress &&
                  showBootActions(deal.state, deal) && (
                    <BootActions deal={deal} renderAsIcon renderAsIconSize={20} />
                  )}
              </VStack>
            </GridItem>
            <GridItem colSpan={5} borderTop="1px solid" borderColor="gray.300" pt={2}>
              <DealTags tags={deal.tags} filterDashboardVisible />
            </GridItem>
            <GridItem colSpan={1} borderTop="1px solid" borderColor="gray.300" pt={2}>
              <Flex justifyContent="center" alignItems="center" h="full">
                <Tooltip
                  label={formatDistanceToNow(
                    deal.lane_arrival_utc ? new Date(deal.lane_arrival_utc) : new Date(),
                  )}
                  placement="top"
                  px={2}
                  py={1}
                  bg="gray.800"
                >
                  <Box>
                    <AiOutlineClockCircle size={14} />
                  </Box>
                </Tooltip>
              </Flex>
            </GridItem>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default Task;
